import React, { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const STATUS_COLORS = {
  pending: "text-[var(--accent)] border-[var(--accent)]",
  active: "text-[var(--primary)] border-[var(--primary)]",
  stopped: "text-[var(--text-2)] border-[var(--text-2)]",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/users/${id}/status`, { status }); toast.success(`Status set to ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setRole = async (id, role) => {
    try { await api.patch(`/users/${id}/role`, { role }); toast.success(`Role set to ${role}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-2">Administration</div>
        <h1 className="font-serif text-4xl">User Management</h1>
        <p className="text-sm text-[var(--text-2)] mt-3 max-w-2xl">
          Approve or revoke access for team members. New sign-ups start as <span className="mono">pending</span>.
        </p>
      </div>

      <div className="card-sharp" data-testid="users-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="ledger-head">Name</TableHead>
              <TableHead className="ledger-head">Email</TableHead>
              <TableHead className="ledger-head">Role</TableHead>
              <TableHead className="ledger-head">Status</TableHead>
              <TableHead className="ledger-head">Joined</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-[var(--bg)]" data-testid={`user-row-${u.id}`}>
                <TableCell className="text-sm">{u.name}</TableCell>
                <TableCell className="text-sm mono">{u.email}</TableCell>
                <TableCell className="text-xs uppercase tracking-widest">{u.role}</TableCell>
                <TableCell>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_COLORS[u.status] || ""}`}>
                    {u.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs mono text-[var(--text-2)]">
                  {(u.created_at || "").slice(0, 10)}
                </TableCell>
                <TableCell>
                  {u.id !== user?.id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="rounded-none" data-testid={`user-actions-${u.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="rounded-none">
                        <DropdownMenuLabel>Status</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => setStatus(u.id, "active")} data-testid={`approve-${u.id}`}>Activate</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(u.id, "stopped")} data-testid={`stop-${u.id}`}>Stop</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatus(u.id, "pending")}>Mark pending</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Role</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => setRole(u.id, "admin")}>Make admin</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setRole(u.id, "member")}>Make member</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => del(u.id)} className="text-[var(--accent)]" data-testid={`delete-${u.id}`}>
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
